import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CircleHelp, FlaskConical, Laptop, Rocket, Save, ShieldCheck, Tv, Users } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { adminApi } from "../../api/admin";
import {
  Badge,
  Banner,
  Button,
  Card,
  Checkbox,
  DataTable,
  EmptyState,
  SearchInput,
  Select,
  SkeletonRows,
  formatRelative,
  useToast,
} from "../../ds";
import type { Device, DeviceGroup, Report, ReportPermissionEntry } from "../../types";
import { SCREEN_STATE, deviceLabel, deviceShortId, isOnline, screenState } from "../admin/labels";

/** The dev/test group (backend setting dev_draft_access_device_group_name). */
const TEST_GROUP = "Laptops";

function sameSet(a: Set<string>, b: Set<string>): boolean {
  return a.size === b.size && [...a].every((x) => b.has(x));
}

function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? "" : "s"}`;
}

/**
 * "Screens & access" tab of the report workspace (admins).
 * Screens: a replace-set (PUT /reports/{id}/screens) of screen groups AND single
 * screens — unticking really removes. People: upserts + real removals. Role
 * grants are never shown or touched here.
 */
export function ScreensAccessTab({ reportId, report }: { reportId: string; report?: Report }) {
  const toast = useToast();
  const qc = useQueryClient();
  const perms = useQuery({ queryKey: ["perms", reportId], queryFn: () => adminApi.listReportPermissions(reportId) });
  const groups = useQuery({ queryKey: ["admin", "device-groups"], queryFn: adminApi.listDeviceGroups });
  const devices = useQuery({ queryKey: ["admin", "devices"], queryFn: adminApi.listDevices, refetchInterval: 30_000 });
  const users = useQuery({ queryKey: ["admin", "users"], queryFn: adminApi.listUsers });

  const server = useMemo(() => {
    const list = perms.data ?? [];
    return {
      groups: new Set(list.filter((e) => e.principal_type === "device_group" && e.can_view).map((e) => e.principal_id)),
      devices: new Set(list.filter((e) => e.principal_type === "device" && e.can_view).map((e) => e.principal_id)),
      people: list.filter((e) => e.principal_type === "user"),
    };
  }, [perms.data]);

  const [groupSel, setGroupSel] = useState<Set<string>>(new Set());
  const [deviceSel, setDeviceSel] = useState<Set<string>>(new Set());
  const [people, setPeople] = useState<ReportPermissionEntry[]>([]);
  const [addUser, setAddUser] = useState("");
  const [search, setSearch] = useState("");
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    if (!perms.data) return;
    setGroupSel(new Set(server.groups));
    setDeviceSel(new Set(server.devices));
    setPeople(server.people);
  }, [perms.data, server]);

  const groupById = useMemo(() => new Map((groups.data ?? []).map((g) => [g.id, g])), [groups.data]);
  const isTestGroup = (id: string | null | undefined) => !!id && groupById.get(id)?.name === TEST_GROUP;
  const screens = useMemo(() => (devices.data ?? []).filter((d) => d.status !== "pending" && d.status !== "revoked"), [devices.data]);
  const inGroup = useMemo(() => {
    const m = new Map<string, Device[]>();
    for (const d of screens) if (d.device_group_id) m.set(d.device_group_id, [...(m.get(d.device_group_id) ?? []), d]);
    return m;
  }, [screens]);

  const screensDirty = !sameSet(groupSel, server.groups) || !sameSet(deviceSel, server.devices);
  const peopleDirty = JSON.stringify([...people].sort((a, b) => a.principal_id.localeCompare(b.principal_id))) !== JSON.stringify([...server.people].sort((a, b) => a.principal_id.localeCompare(b.principal_id)));
  const dirty = screensDirty || peopleDirty;

  // Live preview of "On screens" with the current (unsaved) ticks — same rule as the server:
  // office TVs show the published version; laptops (Laptops group ticked) show the live draft.
  const published = !!report?.current_published_version_id && report?.status === "active";
  const reachedTvs = useMemo(
    () =>
      published
        ? screens
            .filter((d) => d.status === "active" && !isTestGroup(d.device_group_id))
            .filter((d) => deviceSel.has(d.id) || (!!d.device_group_id && groupSel.has(d.device_group_id)))
        : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [screens, deviceSel, groupSel, groupById, published],
  );
  const testReached = report?.status === "active" ? screens.filter((d) => d.status === "active" && isTestGroup(d.device_group_id) && (deviceSel.has(d.id) || groupSel.has(d.device_group_id ?? ""))) : [];
  const reached = [...reachedTvs, ...testReached].sort((a, b) => deviceLabel(a).localeCompare(deviceLabel(b)));
  const reachedList = (list: typeof reached) => list.slice(0, 6).map(deviceLabel).join(", ") + (list.length > 6 ? ` and ${list.length - 6} more` : "");

  const save = useMutation({
    mutationFn: async () => {
      if (screensDirty) await adminApi.setReportScreens(reportId, { device_group_ids: [...groupSel], device_ids: [...deviceSel] });
      if (peopleDirty) {
        const removed = server.people.filter((s) => !people.some((p) => p.principal_id === s.principal_id));
        const changed = people.filter((p) => JSON.stringify(p) !== JSON.stringify(server.people.find((s) => s.principal_id === p.principal_id)));
        if (changed.length) await adminApi.updateReportPermissions(reportId, changed);
        for (const r of removed) await adminApi.removeReportPermission(reportId, "user", r.principal_id);
      }
    },
    onSuccess: () => {
      toast.success("Access saved", published ? (reached.length ? `Showing on ${plural(reached.length, "screen")} within a minute.` : "No office screen shows it now.") : "Screens will show it once it's published.");
      qc.invalidateQueries({ queryKey: ["perms", reportId] });
      qc.invalidateQueries({ queryKey: ["reports"] });
      qc.invalidateQueries({ queryKey: ["report"] });
      qc.invalidateQueries({ queryKey: ["admin", "reports"] });
    },
    onError: (e) => {
      toast.error("Couldn't save access", e instanceof Error ? e.message : undefined);
      qc.invalidateQueries({ queryKey: ["perms", reportId] });
    },
  });

  const toggle = (set: Set<string>, id: string, on: boolean) => {
    const next = new Set(set);
    if (on) next.add(id);
    else next.delete(id);
    return next;
  };
  const setUser = (userId: string, patch: Partial<ReportPermissionEntry> | null) =>
    setPeople((prev) => {
      const cur = prev.find((e) => e.principal_id === userId);
      const rest = prev.filter((e) => e.principal_id !== userId);
      if (patch === null) return rest;
      return [...rest, { principal_type: "user", principal_id: userId, can_view: true, can_edit: false, can_publish: false, can_manage_permissions: false, ...cur, ...patch }];
    });

  const q = search.trim().toLowerCase();
  const sortedScreens = useMemo(
    () =>
      [...screens].sort(
        (a, b) =>
          Number(deviceSel.has(b.id)) - Number(deviceSel.has(a.id)) ||
          Number(isOnline(b)) - Number(isOnline(a)) ||
          deviceLabel(a).localeCompare(deviceLabel(b), undefined, { sensitivity: "base" }),
      ),
    // Order is fixed when the list loads, so rows don't jump while ticking.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [screens, server],
  );
  const filteredScreens = sortedScreens.filter((d) => !q || [deviceLabel(d), deviceShortId(d), groupById.get(d.device_group_id ?? "")?.name ?? "", d.description ?? ""].join(" ").toLowerCase().includes(q));
  const SCREEN_LIMIT = 8;
  const shownScreens = showAll || q ? filteredScreens : filteredScreens.slice(0, SCREEN_LIMIT);
  const userMap = new Map((users.data ?? []).map((u) => [u.id, u]));

  if (perms.isLoading) return <Card><SkeletonRows rows={5} /></Card>;

  const statusLine = !published
    ? testReached.length
      ? { tone: "info" as const, title: `Draft on ${plural(testReached.length, "laptop")}`, body: `${reachedList(testReached)} show the live draft. Office TVs start showing it once you publish.` }
      : { tone: "info" as const, title: "Not published yet — no screen shows it", body: "Choose screens now; they start showing the report as soon as you publish it. Laptops in the Laptops group show the draft right away." }
    : reached.length
      ? { tone: "success" as const, title: `On ${plural(reached.length, "screen")}${testReached.length && reachedTvs.length ? ` (${plural(reachedTvs.length, "TV")}, ${plural(testReached.length, "laptop")})` : ""}`, body: reachedList(reached) }
      : { tone: "warning" as const, title: "Published, but not on any screen", body: "People with access can open it in the browser. Tick a screen group or single screens below to show it on the TVs." };

  return (
    <div className="ds-stack" style={{ gap: 16 }}>
      <Card>
        <div className="ds-grid" style={{ gridTemplateColumns: "minmax(0, 1.1fr) minmax(0, 1fr)", gap: 20, alignItems: "start" }}>
          <div>
            <Banner tone={statusLine.tone} title={statusLine.title + (dirty ? " (after you save)" : "")}>
              {statusLine.body}
            </Banner>
          </div>
          <div className="ds-small" style={{ display: "grid", gap: 8 }}>
            <div className="ds-row" style={{ gap: 8, alignItems: "flex-start" }}>
              <Rocket size={15} className="ds-faint" style={{ marginTop: 2, flexShrink: 0 }} />
              <span><strong>Published</strong> means the latest version is live: people with access can open it in the browser. It isn't on any TV until you choose screens here.</span>
            </div>
            <div className="ds-row" style={{ gap: 8, alignItems: "flex-start" }}>
              <Tv size={15} className="ds-faint" style={{ marginTop: 2, flexShrink: 0 }} />
              <span><strong>On screens</strong> means at least one approved screen shows it: an office TV (published version, through its screen group or chosen on its own) or a laptop in the Laptops group, ticked as a group or one by one (live draft, even before publishing).</span>
            </div>
          </div>
        </div>
      </Card>

      <div className="ds-grid ds-grid--2" style={{ alignItems: "start" }}>
        <Card title="Screen groups" subtitle="Every TV in a ticked group shows the published version — including TVs added to the group later.">
          {(groups.data ?? []).length === 0 ? (
            <EmptyState icon={Tv} title="No screen groups yet" description={<>Create one under <Link to="/admin/device-groups">Screen groups</Link>, then come back.</>} />
          ) : (
            <div className="ds-stack" style={{ gap: 10 }}>
              {(groups.data ?? []).map((g: DeviceGroup) => {
                const members = inGroup.get(g.id) ?? [];
                const active = members.filter((d) => d.status === "active");
                const online = active.filter(isOnline).length;
                const test = g.name === TEST_GROUP;
                return (
                  <label key={g.id} className="ds-card" style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", cursor: "pointer", boxShadow: "none", borderColor: groupSel.has(g.id) ? "var(--accent)" : undefined }}>
                    <Checkbox label="" checked={groupSel.has(g.id)} onChange={(v) => setGroupSel((s) => toggle(s, g.id, v))} />
                    {test ? <Laptop size={18} className="ds-faint" /> : <Tv size={18} className="ds-faint" />}
                    <div className="ds-grow" style={{ minWidth: 0 }}>
                      <div className="ds-row" style={{ gap: 8 }}>
                        <span style={{ fontWeight: 600 }}>{g.name}</span>
                        {test ? <Badge tone="accent"><FlaskConical size={11} /> Test group</Badge> : null}
                      </div>
                      <div className="ds-small ds-muted">
                        {test
                          ? "Builders' laptops. They count as screens and show the live draft, even before publishing."
                          : g.description || "Office TV screens"}
                      </div>
                    </div>
                    <span className="ds-small ds-muted" style={{ whiteSpace: "nowrap", textAlign: "right" }}>
                      {plural(active.length, test ? "laptop" : "screen")}
                      <br />
                      {active.length ? `${online} online` : ""}
                    </span>
                  </label>
                );
              })}
            </div>
          )}
        </Card>

        <Card
          title="Single screens"
          subtitle="Show it on specific TVs or laptops only — without giving the whole group access."
          actions={screens.length > 6 ? <SearchInput value={search} onChange={setSearch} placeholder="Find a screen" width={180} /> : undefined}
        >
          {devices.isLoading ? (
            <SkeletonRows rows={4} />
          ) : screens.length === 0 ? (
            <EmptyState icon={Tv} title="No approved screens yet" description={<>Approve a TV under <Link to="/admin/devices">TV screens</Link> first.</>} />
          ) : (
            <div className="ds-stack" style={{ gap: 6 }}>
              {shownScreens.map((d) => {
                const st = SCREEN_STATE[screenState(d)];
                const group = groupById.get(d.device_group_id ?? "");
                const viaGroup = !!d.device_group_id && groupSel.has(d.device_group_id);
                return (
                  <label key={d.id} className="ds-row" style={{ gap: 12, padding: "8px 10px", borderRadius: "var(--radius-md)", border: "1px solid", borderColor: deviceSel.has(d.id) ? "var(--accent)" : "var(--divider)", cursor: "pointer" }}>
                    <Checkbox label="" checked={deviceSel.has(d.id)} onChange={(v) => setDeviceSel((s) => toggle(s, d.id, v))} />
                    {isTestGroup(d.device_group_id) ? <Laptop size={16} className="ds-faint" /> : <Tv size={16} className="ds-faint" />}
                    <div className="ds-grow" style={{ minWidth: 0 }}>
                      <div className="ds-truncate" style={{ fontWeight: 600 }}>{deviceLabel(d)}</div>
                      <div className="ds-small ds-muted ds-truncate">
                        {group?.name ?? "No group"} · {d.last_seen_at ? `seen ${formatRelative(d.last_seen_at)}` : "not seen yet"}
                        {isTestGroup(d.device_group_id) ? " · shows the live draft" : ""}
                        {d.status === "disabled" ? " · disabled" : ""}
                      </div>
                    </div>
                    {viaGroup ? <Badge tone="neutral" title={`Already shows it because “${group?.name}” is ticked.`}>via group</Badge> : null}
                    <Badge tone={st.tone} dot title={st.hint}>{st.label}</Badge>
                  </label>
                );
              })}
              {filteredScreens.length === 0 ? <div className="ds-small ds-muted">No screen matches “{search}”.</div> : null}
              {!q && filteredScreens.length > SCREEN_LIMIT ? (
                <Button variant="ghost" size="sm" onClick={() => setShowAll((v) => !v)} style={{ alignSelf: "flex-start" }}>
                  {showAll ? "Show fewer" : `Show all ${filteredScreens.length} screens`}
                </Button>
              ) : null}
            </div>
          )}
        </Card>
      </div>

      <Card title="People" subtitle="Who can open, edit or publish this report in the browser. Admins can always do everything.">
        <div className="ds-row" style={{ marginBottom: 14 }}>
          <Select value={addUser} onChange={(e) => setAddUser(e.target.value)} style={{ flex: 1, maxWidth: 520 }}>
            <option value="">Add a person…</option>
            {(users.data ?? []).filter((u) => !people.some((e) => e.principal_id === u.id)).map((u) => (
              <option key={u.id} value={u.id}>{u.display_name || u.email} — {u.email}</option>
            ))}
          </Select>
          <Button icon={Users} disabled={!addUser} onClick={() => { setUser(addUser, { can_view: true }); setAddUser(""); }}>Add</Button>
        </div>
        {people.length === 0 ? (
          <div className="ds-muted ds-small">Nobody added yet.</div>
        ) : (
          <DataTable<ReportPermissionEntry>
            rows={people}
            rowKey={(e) => e.principal_id}
            columns={[
              { key: "who", header: "Person", cell: (e) => { const u = userMap.get(e.principal_id); return <div><div style={{ fontWeight: 600 }}>{u?.display_name ?? "Unknown user"}</div><div className="ds-small ds-muted">{u?.email ?? e.principal_id}</div></div>; } },
              { key: "edit", header: "Can edit", cell: (e) => <Checkbox label="" checked={e.can_edit} onChange={(v) => setUser(e.principal_id, { can_edit: v })} /> },
              { key: "pub", header: "Can publish", cell: (e) => <Checkbox label="" checked={e.can_publish} onChange={(v) => setUser(e.principal_id, { can_publish: v })} /> },
              { key: "rm", header: "", align: "right", cell: (e) => <Button size="sm" variant="ghost" onClick={() => setUser(e.principal_id, null)}>Remove</Button> },
            ]}
          />
        )}
      </Card>

      <div className="ds-row" style={{ position: "sticky", bottom: 0, padding: "12px 0", background: "linear-gradient(transparent, var(--bg, var(--surface)) 30%)" }}>
        <div className="ds-grow ds-small ds-muted">
          {dirty ? (
            <><CircleHelp size={14} style={{ verticalAlign: "-2px" }} /> You have unsaved changes. Unticked screens and removed people lose access when you save.</>
          ) : (
            <><ShieldCheck size={14} style={{ verticalAlign: "-2px" }} /> Changes apply to the published version straight away — no re-publish needed.</>
          )}
        </div>
        <Button
          onClick={() => {
            setGroupSel(new Set(server.groups));
            setDeviceSel(new Set(server.devices));
            setPeople(server.people);
          }}
          disabled={!dirty}
        >
          Discard
        </Button>
        <Button variant="primary" icon={Save} disabled={!dirty} loading={save.isPending} onClick={() => save.mutate()}>Save access</Button>
      </div>
    </div>
  );
}
